import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Hairline, Icon } from "@/components/primitives";

/**
 * Privacy Policy — plain-language summary of what Fig collects, why, and
 * what Collectors can control. Mirrors the FAQ visual language so it feels
 * like part of the field manual rather than a legal afterthought.
 */

interface Section { id: string; title: string; body: string[] }

const LAST_UPDATED = "May 3, 2026";

const SECTIONS: Section[] = [
  {
    id: "summary",
    title: "The short version",
    body: [
      "Fig is a tool for cataloguing, authenticating, and reselling garments. To do that we store the photos you scan, the verdicts FIG's authentication framework returns, and a basic profile (handle, email, follows). We do not sell your data. We do not run third-party ad trackers.",
      "If you ever want your account or any single item deleted, you can do it yourself from Settings, or email us and we will handle it within 30 days.",
    ],
  },
  {
    id: "collect",
    title: "What we collect",
    body: [
      "Account: email, handle, optional avatar, and the auth provider you signed in with.",
      "Content: photos you upload, item titles, brands, conditions, prices, notes, comments, and messages you send other Collectors.",
      "Activity: scans, follows, grails, and which items you opened — used to power your feed and recommendations.",
      "Device: app version, OS, crash reports, and rough region (from IP) for reliability and abuse prevention. No precise GPS.",
    ],
  },
  {
    id: "use",
    title: "How we use it",
    body: [
      "Run FIG's authentication framework on the photos you submit and return a verdict.",
      "Show your closet, feed, grails, and messages back to you and the people you choose to share with.",
      "Suggest comps and prices based on aggregated marketplace data.",
      "Detect abuse, fraud, and counterfeit listings.",
      "Send essential service emails (sign-in, security, replies). Marketing emails are opt-in.",
    ],
  },
  {
    id: "share",
    title: "Who we share with",
    body: [
      "Service providers: hosting, database, authentication, and authentication inference run on Lovable Cloud and the model providers behind it (currently Google and OpenAI). Photos sent for authentication are processed under each provider's enterprise data terms and are not used to train their public models.",
      "Other Collectors: anything you post publicly (your profile, public scans, comments) is visible in-app. Direct messages are visible only to participants.",
      "Law enforcement: only when we are legally required to and, where possible, after notifying you.",
      "We do not sell personal data to advertisers or data brokers.",
    ],
  },
  {
    id: "retention",
    title: "How long we keep it",
    body: [
      "Account and content: until you delete them or close your account.",
      "Deleted items and accounts are purged from primary storage within 30 days and from backups within 90 days.",
      "Aggregated, non-identifying analytics (e.g. 'scans per week') may be kept indefinitely.",
    ],
  },
  {
    id: "rights",
    title: "Your controls",
    body: [
      "Access & export: request a copy of your data from Settings → Privacy.",
      "Edit or delete: every item, comment, and message can be edited or removed from the app.",
      "Close account: Settings → Account → Delete account. This removes your profile, scans, comments, follows, and messages.",
      "If you are in the EU/UK or California, you also have rights under GDPR / CPRA — email privacy@fig.app and we will honor them.",
    ],
  },
  {
    id: "children",
    title: "Children",
    body: [
      "Fig is not directed at people under 13. If you believe a child has signed up, email privacy@fig.app and we will delete the account.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: [
      "If we make material changes, we will notify you in-app and by email at least 14 days before they take effect. The 'last updated' date at the top of this page always reflects the current version.",
    ],
  },
  {
    id: "contact",
    title: "Contact",
    body: [
      "Questions, requests, or concerns: privacy@fig.app",
    ],
  },
];

export default function Privacy() {
  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        {/* Top bar */}
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Privacy</span>
          <div style={{ width: 30 }} />
        </div>

        {/* Hero */}
        <div style={{ padding: "18px 20px 0" }}>
          <div style={{
            position: "relative",
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "20px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              fontWeight: 900, fontSize: 28, lineHeight: 1,
              color: "hsl(var(--ink))", letterSpacing: "-0.02em",
              marginBottom: 8,
            }}>
              Privacy policy
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              marginBottom: 10,
            }}>
              What Fig collects, why we collect it, and the controls you have. Written in plain English, not legalese.
            </div>
            <div className="caps" style={{ color: "hsl(var(--rust))", fontSize: 10 }}>
              Last updated · {LAST_UPDATED}
            </div>
          </div>
        </div>

        {/* Quick jump */}
        <div style={{ padding: "16px 20px 0" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>Jump to</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(s.id)?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "5px 9px",
                  border: "1px solid hsl(var(--ink))",
                  background: "hsl(var(--paper))",
                  color: "hsl(var(--ink))",
                  textDecoration: "none",
                  cursor: "pointer",
                }}
              >
                {s.title}
              </a>
            ))}
          </div>
        </div>

        {/* Sections */}
        <div style={{ padding: "10px 20px 0" }}>
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id} style={{ marginTop: 22, scrollMarginTop: 20 }}>
              <Hairline style={{ marginBottom: 10 }} />
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 900, fontSize: 20,
                color: "hsl(var(--ink))",
                marginBottom: 10,
                letterSpacing: "-0.01em",
              }}>
                {s.title}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {s.body.map((p, i) => (
                  <p key={i} style={{
                    margin: 0,
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 12, lineHeight: 1.55,
                    color: "hsl(var(--ink-2))",
                  }}>
                    {p}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "30px 20px 0" }}>
          <Hairline style={{ marginBottom: 14 }} />
          <div style={{
            background: "hsl(var(--cream))",
            border: "1px solid hsl(var(--ink))",
            padding: "16px 18px",
            textAlign: "center",
          }}>
            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
              Questions?
            </div>
            <div style={{
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 12, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              marginBottom: 12,
            }}>
              Email privacy@fig.app and we'll get back to you.
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button
                onClick={() => nav("/faq")}
                className="btn btn-primary"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Read the FAQ
              </button>
              <button
                onClick={() => nav(-1)}
                className="btn"
                style={{ padding: "8px 14px", fontSize: 11 }}
              >
                Go back
              </button>
            </div>
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
